import React, { useContext, useEffect, useRef } from 'react';
import { FormProvider, type FieldValues, type UseFormReturn } from 'react-hook-form';
import { Box } from '@mui/material';

import { FormStoreContext, FormStore, FormStoreApi, createFormStore } from '@store/FormStore';
import { useStore } from 'zustand';

interface FormProps<TFieldValues extends FieldValues = FieldValues> {
  formId: string;
  methods: UseFormReturn<TFieldValues>;
  children: React.ReactNode;
}
export const Form = <TFieldValues extends FieldValues>({ children, formId, methods }: FormProps<TFieldValues>) => {
  const storeRef = useRef<FormStoreApi>(null);
  if (!storeRef.current) {
    storeRef.current = createFormStore({ formId });
  }

  return (
    <FormStoreContext.Provider value={storeRef.current}>
      <FormBody methods={methods} formId={formId}>
        {children}
      </FormBody>
    </FormStoreContext.Provider>
  );
};

export const useFormStore = <T,>(selector: (store: FormStore) => T): T => {
  const formStoreContext = useContext(FormStoreContext);

  if (!formStoreContext) {
    throw new Error(`useFormStore must be used within Form`);
  }

  return useStore(formStoreContext, selector);
};

const FormBody = <TFieldValues extends FieldValues>({ formId, methods, children }: FormProps<TFieldValues>) => {
  const store = useContext(FormStoreContext);
  if (!store) throw new Error('Missing FormStoreContext.Provider in the tree');

  const { setFormMethods, isReady } = useFormStore((state) => state);

  const formState = methods.formState;
  useEffect(() => {
    setFormMethods({ ...methods, formState });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods, formState]);

  return (
    <FormProvider {...methods}>
      <Box
        component='form'
        data-testid={formId}
        onSubmit={(e) => {
          e.preventDefault();
        }}
        sx={{ width: '100%' }}
        id={formId}
      >
        {isReady ? children : null}
      </Box>
    </FormProvider>
  );
};
